import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { AlertTriangle, X, ChevronRight, Calendar } from "lucide-react";

export const ShortfallAlert = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchAlert = useCallback(async () => {
    try {
      const response = await api.get("/cash-flow/alerts");
      setAlert(response.data);
    } catch (err) {
      console.error("Failed to fetch cash flow alerts:", err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchAlert();
  }, [fetchAlert]);

  const formatCurrency = (value) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-CA", { 
        weekday: "long", 
        month: "long", 
        day: "numeric" 
      });
    } catch {
      return dateStr;
    }
  };

  // Don't render if loading, dismissed, or no alert
  if (loading || dismissed) {
    return null;
  }

  const hasAlert = alert && alert.has_alert;
  const alerts = alert?.alerts || [];
  
  if (!hasAlert || alerts.length === 0) {
    return null;
  }

  const firstAlert = alerts[0];
  const formattedDate = formatDate(firstAlert.date);
  const billsDue = firstAlert.bills_due || [];

  return (
    <Card 
      className="border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 animate-fade-in"
      data-testid="shortfall-alert"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-rose-900 dark:text-rose-100 flex items-center gap-2">
                  Cash Flow Alert
                  <Badge variant="destructive" className="text-xs">
                    {firstAlert.days_until === 1 ? "Tomorrow" : "In " + firstAlert.days_until + " days"}
                  </Badge>
                </h3>
                <p className="text-sm text-rose-700 dark:text-rose-300 mt-1">
                  Your checking account is projected to go <strong>negative</strong> on {formattedDate}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-rose-400 hover:text-rose-600"
                onClick={() => setDismissed(true)}
                data-testid="dismiss-shortfall-alert"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Bills causing shortfall */}
            <div className="mt-3 flex flex-wrap gap-2">
              {billsDue.slice(0, 3).map((bill, i) => (
                <Badge 
                  key={i}
                  variant="outline"
                  className="border-rose-300 bg-white/50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-300"
                >
                  {bill.merchant}: {formatCurrency(bill.amount)}
                </Badge>
              ))}
              {billsDue.length > 3 && (
                <Badge variant="outline" className="border-rose-300 text-rose-600">
                  +{billsDue.length - 3} more
                </Badge>
              )}
            </div>
            
            {/* Projected balance */}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-rose-600 dark:text-rose-400">Projected balance: </span>
                <span className="font-bold text-rose-700 dark:text-rose-300">
                  {formatCurrency(firstAlert.projected_balance)}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-100 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/50"
                onClick={() => navigate("/bills")}
                data-testid="view-bills-btn"
              >
                <Calendar className="h-4 w-4 mr-1" />
                View Bills
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
